import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useApiQuery } from "app/modules/api-client";
import { INDUSTRY_ENDPOINT_MIN_DATE } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";

import { useIndustryAvailabilityParams } from "../useIndustryAvailabilityParams";

export interface IIndustryPublisherRow {
    publisher: string;
    category: string;
    subCategory: string;
    rank: number;
    siteKey: string;
    spend: number;
    spendShare: number;
    spendChange: number;
    isSpendNew: boolean;
    impressions: number;
    impressionsShare: number;
    impressionsChange: number;
    isImpressionNew: boolean;
    visits: number;
    visitsShare: number;
    visitsChange: number;
    isVisitsNew: boolean;
}

// Server-side paginated/searched/sorted. `search` (publisher name) and `sort`/`asc`
// are forwarded to the endpoint; `sort` must be a server key — the caller adapts its
// column keys before passing them in (see SERVER_SORT_KEY_BY_COLUMN in IndustryPublishers.tsx).
export const useIndustryTopPublishersTable = (
    category: IFlattenedCategory,
    page: number,
    pageSize: number,
    search?: string,
    sort?: string,
    asc?: boolean,
    publisherIndustries?: string[],
) => {
    const { params, availability } = useIndustryAvailabilityParams(
        category,
        INDUSTRY_ENDPOINT_MIN_DATE.adintelligenceIndustryTopPublishersList,
    );

    const { data, isLoading, isFetching, isError } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName: "adintelligenceIndustryTopPublishersList",
            options: {
                enabled: !availability.isFullyUnavailable,
                select: (response) => ({
                    rows: (response?.data ?? []).map((item) => ({
                        publisher: item.publisher ?? "",
                        category: item.category ?? "",
                        subCategory: item.subCategory ?? "",
                        rank: item.rank ?? 0,
                        siteKey: item.siteKey ?? "",
                        spend: item.spend ?? 0,
                        spendShare: item.spendShare ?? 0,
                        spendChange: item.spendChange ?? 0,
                        isSpendNew: item.isSpendNew ?? false,
                        impressions: item.impressions ?? 0,
                        impressionsShare: item.impressionsShare ?? 0,
                        impressionsChange: item.impressionsChange ?? 0,
                        isImpressionNew: item.isImpressionNew ?? false,
                        visits: item.visits ?? 0,
                        visitsShare: item.visitsShare ?? 0,
                        visitsChange: item.visitsChange ?? 0,
                        isVisitsNew: item.isVisitsNew ?? false,
                    })),
                    totalCount: response?.totalCount ?? 0,
                }),
            },
        },
        {
            ...params,
            page,
            pageSize,
            ...(search ? { search } : {}),
            ...(sort ? { sort, asc: !!asc } : {}),
            ...(publisherIndustries?.length ? { publisherIndustries } : {}),
        },
    );

    return {
        rows: data?.rows ?? [],
        totalCount: data?.totalCount ?? 0,
        isLoading: isLoading || isFetching,
        isError,
        availability,
    };
};
